// src/pages/Signup.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { UserPlus, Mail, Lock, Eye, EyeOff, User, Phone, ArrowLeft, Loader2 } from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Username validation states (client-side only)
  const [usernameError, setUsernameError] = useState('');

  // Validate username format only (no Firebase check)
  React.useEffect(() => {
    if (!userName || userName.length < 3) {
      setUsernameError('');
      return;
    }

    if (!/^[a-z0-9]+$/.test(userName)) {
      setUsernameError('Only lowercase letters and numbers allowed');
      return;
    }

    setUsernameError('');
  }, [userName]);

  const generateUsername = (fullName: string) => {
    return fullName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 15);
  };

  const validateForm = () => {
    if (!name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Invalid email format');
      return false;
    }
    if (!phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      setError('Valid Indian mobile number required (10 digits, starts with 6-9)');
      return false;
    }
    if (!userName.trim()) {
      setError('Username is required');
      return false;
    }
    if (userName.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }
    if (userName.length > 14) {
      setError('Username must be lessthan 14 characters');
      return false;
    }
    if (!/^[a-z0-9]+$/.test(userName)) {
      setError('Username can only contain lowercase letters and numbers');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

const handleSignup = async (e: React.FormEvent) => {
  e.preventDefault();
  setError('');

  if (!validateForm()) return;

  setLoading(true);

  // Track ONLY the user created in THIS signup
  let createdUser: any = null;

  try {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedUsername = userName.trim().toLowerCase();
    const normalizedName = name.trim();
    const normalizedPhone = phone.trim();

    const functions = getFunctions();

    // ============================================
    // STEP 1: Check username BEFORE creating Auth
    // ============================================
    const checkUsernameAvailability = httpsCallable(
      functions,
      'checkUsernameAvailability'
    );

    const usernameResult = await checkUsernameAvailability({
      userName: normalizedUsername
    });

    const usernameResponse = usernameResult.data as {
      available: boolean;
    };

    if (!usernameResponse.available) {
      setError('Username already taken. Please choose another.');
      setLoading(false);
      return;
    }

    // ============================================
    // STEP 2: Now create Firebase Auth user
    // ============================================
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      normalizedEmail,
      password
    );

    createdUser = userCredential.user; // Track THIS user

    // ============================================
    // STEP 3: Update display name
    // ============================================
    await updateProfile(createdUser, {
      displayName: normalizedName
    });

    // ============================================
    // STEP 4: Create database account
    // ============================================
    const createUserAccount = httpsCallable(
      functions,
      'createUserAccount'
    );

    const result = await createUserAccount({
      name: normalizedName,
      userName: normalizedUsername,
      email: normalizedEmail,
      phone: normalizedPhone
    });

    const response = result.data as {
      success: boolean;
      message: string;
    };

    if (!response.success) {
      throw new Error('ACCOUNT_CREATION_FAILED');
    }

    // ============================================
    // STEP 5: Navigate only after everything succeeds
    // ============================================
    navigate('/dashboard', { replace: true });

  } catch (err: any) {
    console.error('Signup error:', err);

    // ============================================
    // IMPORTANT: Delete ONLY the user created here
    // NOT auth.currentUser (could be different user!)
    // ============================================
    if (createdUser) {
      try {
        await createdUser.delete();
      } catch (deleteError) {
        console.error('Failed to rollback Auth user:', deleteError);
      }
    }


// Firebase Auth Errors
if (err.code === 'auth/email-already-in-use' || err.message?.includes('email-already-in-use')) {
  setError('Email already registered. Please login instead.');
} else if (err.code === 'auth/invalid-email' || err.message?.includes('invalid-email')) {
  setError('Invalid email format.');
} else if (err.code === 'auth/weak-password' || err.message?.includes('weak-password')) {
  setError('Password is too weak. Use at least 8 characters.');
} else if (err.code === 'auth/user-not-found' || err.message?.includes('user-not-found')) {
  setError('Account not found. Please sign up first.');
} else if (err.code === 'auth/wrong-password' || err.message?.includes('wrong-password')) {
  setError('Incorrect password.');
} else if (err.code === 'auth/too-many-requests' || err.message?.includes('too-many-requests')) {
  setError('Too many failed attempts. Please try again later.');
}
// Cloud Function Errors
else if (
  err.code === 'functions/already-exists' ||
  err.message?.includes('already-exists') ||
  err.message?.includes('USERNAME_TAKEN')
) {
  setError('Username already taken. Please choose another.');
} else if (err.code === 'functions/unavailable' || err.message?.includes('UNAVAILABLE')) {
  setError('Service temporarily unavailable. Please try again.');
}
// Account Creation Failed
else if (err.message?.includes('ACCOUNT_CREATION_FAILED')) {
  setError('Account creation failed. Please try again.');
}
// Unknown Error
else {
  console.error('Unknown error:', err);
  setError('Signup failed. Please try again.');
}
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-[#0A0F1E] flex items-center justify-center px-4 py-8">
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <button onClick={() => navigate('/')} className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>

        <div className="bg-[#1E2338] rounded-2xl border border-[#2A2F45] p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-rajdhani font-bold text-white">Create Account</h1>
            <p className="text-gray-400 mt-2">Join TheArenaX and start winning</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!userName) {
                      setUserName(generateUsername(e.target.value));
                    }
                  }}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition"
                  placeholder="Enter 10-digit phone number"
                />
              </div>
              <p className="text-gray-500 text-xs mt-1">Used for withdrawals and support</p>
            </div>

            {/* Username */}
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">
                Username 
                <span className="text-gray-500 text-xs ml-2">(unique, lowercase letters & numbers only)</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  required
                  className={`w-full bg-[#0A0F1E] border rounded-xl px-12 py-3 text-white focus:outline-none transition ${
                    usernameError 
                      ? 'border-red-500' 
                      : userName.length >= 3
                      ? 'border-green-500'
                      : 'border-[#2A2F45] focus:border-[#00fff5]'
                  }`}
                  placeholder="Choose a unique username (checked on signup)"
                />
              </div>
              {usernameError && (
                <p className="text-xs mt-1 text-red-500">
                  {usernameError}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition"
                  placeholder="Create a password (min 8 chars)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500 rounded-xl p-3">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#00fff5] to-[#7000ff] py-3 rounded-xl text-white font-rajdhani font-bold text-lg hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Sign Up'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-[#00fff5] hover:underline font-semibold">
                Login
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-[#00fff5] hover:underline">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="text-[#00fff5] hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Signup;